/**
 * Expand Macro — replace a collapsed macro node with its raw member nodes.
 *
 * The members are generated by trace-viewer's own `expandMacro`, driven by
 * the `MacroDefinition` (nodes, wires, value wires, constants, private events
 * and variables), written into ReactFlow nodes/edges through a
 * `MacroGraphWriter`. The canonical shape is therefore defined once, in
 * trace-viewer, and this works for every registered macro.
 *
 * ONE-WAY by design: the expanded members carry no macro metadata, so the next
 * save stores them as plain untagged nodes. Recognition never collapses this
 * instance again (degrade-by-design; see trace-viewer macros/). There is
 * deliberately no "re-collapse" affordance.
 */

import type { Node, Edge } from 'reactflow';
import { MACRO_PARAM_WIRED, expandMacro, getMacroByOp, GraphOps } from 'trace-viewer/behaviors';
import type { GraphVariable, MacroGraphWriter } from 'trace-viewer/behaviors';
import { ALL_TYPE_SIGNATURES, applyDagreLayout, type FlowNodeData } from './graph-conversion';
import { getNodeDef } from './node-definitions';

const FLOW_EDGE_STYLE = { stroke: '#f59e0b', strokeWidth: 2 };
const VALUE_EDGE_STYLE = { stroke: '#64748b', strokeWidth: 1, strokeDasharray: '5 3' };

/** Graph state the expansion adds to: private variables and events land here. */
export interface MacroExpandContext {
    /** The editor's variables (canonical type indices). The expansion's private variables are appended. */
    variables?: GraphVariable[];
    /** Number of custom events the edited graph already has (`GraphData.events.length`). */
    eventCount?: number;
}

export interface MacroExpandResult {
    nodes: Node<FlowNodeData>[];
    edges: Edge[];
    nextNodeId: number;
    /** `context.variables` plus the expansion's private variables (reused by name when already present). */
    variables: GraphVariable[];
}

/**
 * A `MacroGraphWriter` emitting ReactFlow nodes/edges. Node indices it returns
 * are positions in `nodes`. Events are addressed by index only — the editor
 * holds no event list; `reactFlowToGraph` materializes an internal event for
 * each index a send/receive node addresses past the stored ones.
 */
class FlowNodeWriter implements MacroGraphWriter {
    readonly nodes: Node<FlowNodeData>[] = [];
    readonly edges: Edge[] = [];

    constructor(
        private _nextNodeId: number,
        private _nextEvent: number,
        readonly variables: GraphVariable[],
    ) {}

    get nextNodeId(): number {
        return this._nextNodeId;
    }

    _addNode(op: string, config: Record<string, unknown[]>): number {
        const def = getNodeDef(op);
        const flat: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(config)) flat[key] = value.length === 1 ? value[0] : value;
        this.nodes.push({
            id: `node-${this._nextNodeId++}`,
            type: def?.category ?? 'execution',
            position: { x: 0, y: 0 },
            data: { op, label: def?.label ?? op, config: flat, inlineValues: {} },
        });
        return this.nodes.length - 1;
    }

    _setFlow(fromNode: number, fromSocket: string, toNode: number, toSocket = 'in'): void {
        const source = this.nodes[fromNode].id;
        const target = this.nodes[toNode].id;
        this.edges.push({
            id: `${source}-${fromSocket}-${target}-${toSocket}`,
            source, sourceHandle: `flow-out-${fromSocket}`,
            target, targetHandle: `flow-in-${toSocket}`,
            type: 'default', animated: true, style: FLOW_EDGE_STYLE,
        });
    }

    _setValue(nodeIndex: number, socket: string, value: unknown): void {
        this.nodes[nodeIndex].data.inlineValues[socket] = [value];
    }

    _setValueRef(nodeIndex: number, socket: string, sourceNode: number, sourceSocket = 'value'): void {
        const source = this.nodes[sourceNode].id;
        const target = this.nodes[nodeIndex].id;
        this.edges.push({
            id: `value-${source}-${sourceSocket}-${target}-${socket}`,
            source, sourceHandle: `data-out-${sourceSocket}`,
            target, targetHandle: `data-in-${socket}`,
            type: 'default', style: VALUE_EDGE_STYLE,
        });
    }

    _addEvent(): number {
        return this._nextEvent++;
    }

    _addVariable(name: string, signature: string, value: unknown): number {
        const canonical = ALL_TYPE_SIGNATURES.indexOf(signature as (typeof ALL_TYPE_SIGNATURES)[number]);
        const variable = { name, type: canonical >= 0 ? canonical : 0, value: [value] };
        // Expanding keeps the instance id, so a private variable the collapsed
        // instance already owned is taken over rather than left as an orphan.
        const existing = this.variables.findIndex(v => v.name === name);
        if (existing !== -1) {
            this.variables[existing] = variable;
            return existing;
        }
        this.variables.push(variable);
        return this.variables.length - 1;
    }
}

/** First event index no send/receive node on the canvas (or stored graph) uses yet. */
function firstFreeEvent(nodes: readonly Node<FlowNodeData>[], eventCount: number): number {
    let next = eventCount;
    for (const n of nodes) {
        if (n.data.op !== GraphOps.EVENT_SEND && n.data.op !== GraphOps.EVENT_RECEIVE) continue;
        const index = n.data.config['event'];
        if (typeof index === 'number' && Number.isInteger(index) && index >= next) next = index + 1;
    }
    return next;
}

/**
 * Expand a collapsed macro node (any registered `macro/…` op) in place into
 * its canonical expansion, shown raw. External edges are rewired onto the
 * members through the definition's `inputs` / `outputs` / `params`; a wired
 * param keeps its source and feeds every target socket.
 *
 * @returns null when `macroNodeId` is not a macro node.
 */
export function expandMacroNode(
    allNodes: Node<FlowNodeData>[],
    allEdges: Edge[],
    macroNodeId: string,
    nextNodeId: number,
    context: MacroExpandContext = {},
): MacroExpandResult | null {
    const macroNode = allNodes.find(n => n.id === macroNodeId);
    const def = macroNode ? getMacroByOp(macroNode.data.op) : undefined;
    if (!macroNode || !def) return null;

    // Avoid id collisions with existing nodes (mirrors inlineSubgraph).
    for (const n of allNodes) {
        const num = parseInt(n.id.replace(/^node-(?:sg-)?/, ''), 10);
        if (!isNaN(num) && num >= nextNodeId) nextNodeId = num + 1;
    }

    // Params: wired ones keep their edge; literals come off the node.
    const wiredEdges = allEdges.filter(e => e.target === macroNodeId && e.targetHandle?.startsWith('data-in-'));
    const params: Record<string, unknown> = {};
    for (const [name, param] of Object.entries(def.params)) {
        if (wiredEdges.some(e => e.targetHandle === `data-in-${name}`)) params[name] = MACRO_PARAM_WIRED;
        else if (param.kind === 'config') params[name] = macroNode.data.config[name];
        else params[name] = macroNode.data.inlineValues[name]?.[0];
    }

    const writer = new FlowNodeWriter(
        nextNodeId,
        firstFreeEvent(allNodes, context.eventCount ?? 0),
        [...(context.variables ?? [])],
    );
    // An invalid literal disarms the expansion exactly as commit-time lowering
    // would — the author sees the same inert composition either way.
    // The instance id is kept so the private variables it already owns (by
    // name) are reused; the members are untagged regardless.
    const { nodes: roles } = expandMacro(writer, def, params, macroNode.data.macroId);
    const idOf = (role: string): string => writer.nodes[roles[role]].id;

    // Lay the members out on their own, then place them where the macro was.
    applyDagreLayout(writer.nodes, writer.edges);
    const minX = Math.min(...writer.nodes.map(n => n.position.x));
    const minY = Math.min(...writer.nodes.map(n => n.position.y));
    for (const n of writer.nodes) {
        n.position = { x: n.position.x - minX + macroNode.position.x, y: n.position.y - minY + macroNode.position.y };
    }

    const newEdges: Edge[] = [...writer.edges];
    for (const edge of allEdges) {
        if (edge.target === macroNodeId) {
            if (edge.targetHandle?.startsWith('flow-in-')) {
                const socket = edge.targetHandle.slice('flow-in-'.length);
                const ref = def.inputs[socket] ?? def.inputs[def.defaultInput];
                newEdges.push({ ...edge, id: `${edge.id}-xm`, target: idOf(ref.role), targetHandle: `flow-in-${ref.socket}` });
            } else if (edge.targetHandle?.startsWith('data-in-')) {
                const param = def.params[edge.targetHandle.slice('data-in-'.length)];
                param?.targets.forEach((t, k) => {
                    newEdges.push({ ...edge, id: `${edge.id}-xm${k}`, target: idOf(t.role), targetHandle: `data-in-${t.socket}` });
                });
            }
        } else if (edge.source === macroNodeId) {
            const ref = edge.sourceHandle?.startsWith('flow-out-')
                ? def.outputs[edge.sourceHandle.slice('flow-out-'.length)]
                : undefined;
            if (ref) newEdges.push({ ...edge, id: `${edge.id}-xm`, source: idOf(ref.role), sourceHandle: `flow-out-${ref.socket}` });
        } else {
            newEdges.push(edge);
        }
    }

    // Members wired past their static def (a Sequence using sockets '2', '3')
    // need those sockets surfaced so the edges have handles to land on.
    for (const n of writer.nodes) {
        const nodeDef = getNodeDef(n.data.op);
        const outs = new Set<string>();
        const ins = new Set<string>();
        for (const e of newEdges) {
            if (e.source === n.id && e.sourceHandle?.startsWith('flow-out-')) outs.add(e.sourceHandle.slice('flow-out-'.length));
            if (e.target === n.id && e.targetHandle?.startsWith('flow-in-')) ins.add(e.targetHandle.slice('flow-in-'.length));
        }
        const flowOutputs = [...outs].filter(s => !nodeDef?.flowOutputs.includes(s));
        const flowInputs = [...ins].filter(s => !nodeDef?.flowInputs.includes(s));
        if (flowOutputs.length > 0 || flowInputs.length > 0) {
            n.data.dynamicSockets = { flowInputs, flowOutputs, dataInputs: [], dataOutputs: [] };
        }
    }

    return {
        nodes: allNodes.filter(n => n.id !== macroNodeId).concat(writer.nodes),
        edges: newEdges,
        nextNodeId: writer.nextNodeId,
        variables: writer.variables,
    };
}
